import { Group, Stack, Textarea } from "@mantine/core";
import { CircleDot, Clock3, Infinity as Loop } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { CatalogAudioModel, Job } from "../../../api/generated/model";
import { AudioSfxQuote, AudioSfxRequest } from "../../../api/generated/zod";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { UsageNotes } from "../../../ui/UsageNotes";
import { SoundErrors, StartRow } from "./StartRow";
import { stepsOf } from "./steps";
import { ToggleChip } from "./ToggleChip";
import { useSoundJob } from "./use-sound-job";

/** Live's words (`sound-effects/constants.ts`, capture 01). */
const PLACEHOLDER = "A futuristic spaceship engine powering up with mechanical humming.";
const TIPS = {
  model: "Select the model for sound effect generation.",
  loop: "Create a seamlessly looping sound that's ideal for background ambience and continuous playback.",
  duration:
    "Choose how long the generated sound should be. Auto lets the model determine the best duration.",
  influence:
    "Controls how closely the generated sound follows your prompt. Higher values prioritize your instructions, while lower values allow the AI more creative freedom",
} as const;

export interface SoundFormProps {
  models: CatalogAudioModel[];
  /** The catalog's voice design model (speech's Create Voice), or none. */
  voiceDesign: CatalogAudioModel | undefined;
  /** The mode pills above the panel. */
  above: ReactNode;
  onStarted: (job: Job) => void;
  /** In a card of its own, not floating (`GlassPanel`'s `inline`, the Pro Studio redesign). */
  inline?: boolean | undefined;
}

/**
 * Live's Sound effects composer (#235, capture 01): the description, then the model, the loop
 * chip, the length (0.5 to 30 seconds), the prompt's influence (0% to 100%), the usage notes,
 * the count, the price and the start. Each option comes from the model's block (G7).
 */
export function SoundEffectsForm({ models, above, onStarted, inline }: SoundFormProps) {
  const [modelId, setModelId] = useState(defaultOf(models).id);
  const model = models.find((each) => each.id === modelId) ?? defaultOf(models);
  const block = model.sound_effects!;
  const [prompt, setPrompt] = useState("");
  const [seconds, setSeconds] = useState(block.duration_seconds.default);
  const [loop, setLoop] = useState(block.loop_default);
  const [influence, setInfluence] = useState(block.prompt_influence?.default ?? null);
  // Another model starts at its own block's defaults, as live's form resets them.
  const choose = (id: string) => {
    const next = models.find((each) => each.id === id)?.sound_effects;
    if (next === undefined || next === null) return;
    setModelId(id);
    setSeconds(next.duration_seconds.default);
    setLoop(next.loop_default);
    setInfluence(next.prompt_influence?.default ?? null);
  };
  const [collapsed, setCollapsed] = useState(false);
  const fields = {
    type: "audio.sfx" as const,
    model: model.id,
    duration_seconds: seconds,
    ...(block.loop ? { loop } : {}),
    ...(block.prompt_influence === null || influence === null
      ? {}
      : { prompt_influence: influence }),
  };
  const quote = AudioSfxQuote.safeParse(fields);
  // Live's form needs 10 characters of description (`prompt_min_chars`).
  const written = prompt.trim();
  const request =
    written.length >= block.prompt_min_chars
      ? AudioSfxRequest.safeParse({ ...fields, prompt: written })
      : undefined;
  // The price never reads the description: the quote sends none (as live's estimate).
  const job = useSoundJob(
    quote.success ? fields : undefined,
    request?.success === true ? request.data : undefined,
    written !== "",
    onStarted,
  );
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      summary={<span>Sound effects</span>}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={12} wrap="nowrap">
            <ModelPicker
              models={models}
              value={model.id}
              onChange={choose}
              bolts={false}
              tip={TIPS.model}
            />
            {block.loop ? (
              <ToggleChip
                setting="Loop"
                icon={<Loop size={20} strokeWidth={1.5} aria-hidden />}
                on={loop}
                onChange={setLoop}
                tip={TIPS.loop}
              />
            ) : null}
            <Choice
              setting="Duration"
              icon={<Clock3 size={20} strokeWidth={1.5} />}
              chevron={false}
              width={96}
              tip={TIPS.duration}
              value={String(seconds)}
              options={stepsOf(block.duration_seconds).map((each) => ({
                value: String(each),
                label: `${each} sec`,
              }))}
              onChange={(next) => setSeconds(Number(next))}
            />
            {block.prompt_influence === null || influence === null ? null : (
              <Choice
                setting="Prompt influence"
                icon={<CircleDot size={20} strokeWidth={1.5} />}
                chevron={false}
                width={80}
                tip={TIPS.influence}
                value={String(influence)}
                options={stepsOf(block.prompt_influence).map((each) => ({
                  value: String(each),
                  label: `${Math.round(each * 100)}%`,
                }))}
                onChange={(next) => setInfluence(Number(next))}
              />
            )}
            <UsageNotes notes={block.notes} />
          </Group>
          <StartRow job={job} />
        </Group>
      }
    >
      <Stack gap={4}>
        <Textarea
          variant="unstyled"
          aria-label="Prompt"
          placeholder={PLACEHOLDER}
          autosize
          minRows={1}
          maxRows={5}
          maxLength={model.prompt_max_chars}
          value={prompt}
          onChange={(event) => setPrompt(event.currentTarget.value)}
        />
        <SoundErrors job={job} />
      </Stack>
    </GlassPanel>
  );
}

/** The catalog's default model for a mode, else its first (live's). */
export function defaultOf(models: CatalogAudioModel[]): CatalogAudioModel {
  return models.find((model) => model.is_default) ?? models[0]!;
}
